/**
 * Browsers do not paint hidden tabs. A page opened in the background ("open in new tab", session restore, etc) will
 * emit FCP/LCP whenever the user switch to look at the tab, inflating the reported value, sometimes to hours.
 * So these values need to be dropped.
 *
 * web-vitals already guards against this by capturing the time the page was first hidden,
 * but that was done lazily on the first onLCP()/onFCP() call.
 * Next's useReportWebVitals call those from useEffect, so the timestamp is only taken after React hydration.
 * A tab that is backgrounded before hydration finishes has no timestamp recorded, and thus the guard fell through.
 *
 * The fix is to take the timestamp ourselves during HTML parsing, before React bundles evaluate.
 */

export const FIRST_HIDDEN_GLOBAL = '__BNEF_FIRST_HIDDEN__';

// Run synchronously in <head> to records the first moment the page became hidden
// Ref: https://github.com/GoogleChrome/web-vitals/blob/v4.2.4/src/lib/getVisibilityWatcher.ts#L19-L30
// https://github.com/GoogleChrome/web-vitals/blob/v4.2.4/src/lib/getVisibilityWatcher.ts#L64-L71
export const recordFirstHiddenScript = `
    (() => {
        var w = window;
        w.${FIRST_HIDDEN_GLOBAL} = document.visibilityState === 'hidden' ? 0 : Infinity;
        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.visibilityState === 'hidden' && w.${FIRST_HIDDEN_GLOBAL} === Infinity) {
                    w.${FIRST_HIDDEN_GLOBAL} = performance.now();
                }
            },
            true
        );
    })();
`
    .replace(/\s*\n\s*/g, '') // Trim each line to minify a bit
    .trim();

// Falls back to Infinity, so metrics aren't silently dropped on error.
export const getFirstHiddenTime = (): number => {
    if (typeof window === 'undefined') return Infinity;
    const value = (window as unknown as Record<string, unknown>)[FIRST_HIDDEN_GLOBAL];
    return typeof value === 'number' ? value : Infinity;
};

const NAVIGATION_RELATIVE_METRICS = new Set(['FCP', 'LCP']);

// Ref: https://github.com/GoogleChrome/web-vitals/blob/v4.2.4/src/onLCP.ts#L66-L67
export const isBackgroundTabArtifact = (name: string, value: number): boolean =>
    NAVIGATION_RELATIVE_METRICS.has(name) && value > getFirstHiddenTime();
